import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import {
  Home as HomeIcon,
  Shield,
  DollarSign,
  Heart,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { colors } from "../config/site";

const RESOURCE_SECTIONS = [
  {
    id: "homelessness",
    title: "Homelessness Resources",
    subtitle: "Immediate Help",
    description:
      "Emergency shelter, food assistance, and support services for those facing homelessness.",
    icon: Heart,
    accent: colors.terracotta,
    linkTo: "/resources/homelessness",
    points: ["24/7 Homeless Hotline", "Emergency Shelter Locations", "Food Assistance Programs", "Benefits & Support Services"],
  },
  {
    id: "renters",
    title: "Resources for Renters",
    subtitle: "Start Here",
    description: "Essential tools and guides for successful renting, tenant rights, and housing stability.",
    icon: HomeIcon,
    accent: colors.primary,
    linkTo: "/resources/renters",
    points: ["Tenant Rights Guide (PDF)", "Move-In Checklist", "Rental Budget Calculator", "Utility Setup Guide"],
  },
  {
    id: "homeowners",
    title: "Resources for Homeowners",
    subtitle: "Next Phase",
    description: "Guides for current homeowners and those planning for future homeownership.",
    icon: Shield,
    accent: colors.sage,
    linkTo: "/resources/homeowners",
    points: ["First-Time Homeowner Guide", "Home Maintenance Schedule", "Energy Efficiency Tips", "Home Insurance Guide"],
  },
  {
    id: "financial",
    title: "Financial Resources",
    subtitle: "Essential Tools",
    description: "Budget planning, credit building, and financial literacy tools that support housing stability.",
    icon: DollarSign,
    accent: colors.gold,
    linkTo: "/resources/financial",
    points: ["Budget Planning Templates", "Credit Improvement Workbook", "Savings Challenge Guides", "Trusted Financial Partners"],
  },
];

const ResourceCenter = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />

      <main className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-7xl">
          {/* Intro */}
          <div className="mb-14 max-w-3xl">
            <p
              className="text-xs uppercase tracking-widest font-semibold mb-3"
              style={{ color: colors.terracotta }}
            >
              Resource Center
            </p>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-5"
              style={{ color: colors.charcoal }}
            >
              Tools and education for every step of the housing journey.
            </h1>
            <p className="text-lg" style={{ color: colors.textMuted }}>
              Practical guides, interactive learning tools, and partner referrals — built to
              grow with our community.
            </p>
          </div>

          {/* Sections */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
            {RESOURCE_SECTIONS.map(({ id, title, subtitle, description, icon: Icon, accent, linkTo, points }) => (
              <Card
                key={id}
                className="border-0 shadow-sm hover:shadow-md transition-all relative overflow-hidden"
                style={{ backgroundColor: colors.white, borderTop: `3px solid ${accent}` }}
                data-testid={`resource-card-${id}`}
              >
                <div
                  className="absolute top-4 right-4 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
                  style={{ backgroundColor: accent }}
                >
                  {subtitle}
                </div>

                <CardHeader className="pb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                    style={{ backgroundColor: accent }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg font-bold" style={{ color: colors.charcoal }}>
                    {title}
                  </CardTitle>
                  <p className="text-sm mt-2" style={{ color: colors.textMuted }}>{description}</p>
                </CardHeader>

                <CardContent className="space-y-5">
                  <ul className="space-y-1.5">
                    {points.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-sm" style={{ color: colors.charcoal }}>
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: accent }}></span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={linkTo}>
                    <Button
                      className="w-full text-white rounded-full py-5 font-semibold text-sm shadow-sm hover:shadow-md transition-all"
                      style={{ backgroundColor: accent }}
                    >
                      Explore Resources
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Learning Modules — the curriculum architecture */}
          <section
            className="rounded-3xl p-8 lg:p-10 shadow-md mb-8"
            style={{ backgroundColor: colors.white, borderLeft: `5px solid ${colors.terracotta}` }}
            data-testid="games-quizzes-hub"
          >
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-5 h-5" style={{ color: colors.terracotta }} />
              <span
                className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                style={{ backgroundColor: colors.terracotta + "22", color: colors.terracottaDark }}
              >
                Interactive Curriculum
              </span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold mb-2" style={{ color: colors.charcoal }}>
              Interactive Learning Tools
            </h2>
            <p className="mb-7 max-w-2xl" style={{ color: colors.textMuted }}>
              Short, hands-on activities and real-life case studies across five modules.
              No signup. Instant feedback.
            </p>

            {/* Module 1: Ready to Rent */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
                  style={{ backgroundColor: colors.primary, color: colors.white }}
                >
                  Module 1
                </span>
                <h3 className="text-lg font-bold" style={{ color: colors.charcoal }}>
                  Ready to Rent
                </h3>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <Link
                  to="/quiz/rent-readiness"
                  className="group rounded-2xl p-5 border-2 transition-all hover:shadow-md flex flex-col"
                  style={{ borderColor: colors.sage, backgroundColor: colors.cream }}
                  data-testid="hub-quiz-rent-readiness"
                >
                  <div
                    className="inline-block w-fit px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2"
                    style={{ backgroundColor: colors.sage + "33", color: colors.primary }}
                  >
                    Quiz · 3 min
                  </div>
                  <h4 className="text-base font-bold mb-1.5" style={{ color: colors.charcoal }}>
                    Rent Readiness Quiz
                  </h4>
                  <p className="text-xs mb-4 flex-grow" style={{ color: colors.textMuted }}>
                    Ten questions to score your readiness to rent with personalized tips.
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-bold group-hover:gap-2 transition-all"
                    style={{ color: colors.primary }}
                  >
                    Take the Quiz <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
                <Link
                  to="/games/lease-detective"
                  className="group rounded-2xl p-5 border-2 transition-all hover:shadow-md flex flex-col"
                  style={{ borderColor: colors.terracotta, backgroundColor: colors.ivory }}
                  data-testid="hub-game-lease-detective"
                >
                  <div
                    className="inline-block w-fit px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2"
                    style={{ backgroundColor: colors.terracotta + "22", color: colors.terracottaDark }}
                  >
                    Simulation · 5 min
                  </div>
                  <h4 className="text-base font-bold mb-1.5" style={{ color: colors.charcoal }}>
                    Lease Detective
                  </h4>
                  <p className="text-xs mb-4 flex-grow" style={{ color: colors.textMuted }}>
                    Spot fair vs. unfair lease clauses. Learn your rights along the way.
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-bold group-hover:gap-2 transition-all"
                    style={{ color: colors.terracottaDark }}
                  >
                    Start Simulation <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
                <Link
                  to="/games/apartment-shopping"
                  className="group rounded-2xl p-5 border-2 transition-all hover:shadow-md flex flex-col"
                  style={{ borderColor: colors.gold, backgroundColor: colors.cream }}
                  data-testid="hub-game-apartment-shopping"
                >
                  <div
                    className="inline-block w-fit px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2"
                    style={{ backgroundColor: colors.gold + "44", color: colors.charcoal }}
                  >
                    Simulation · 4 min
                  </div>
                  <h4 className="text-base font-bold mb-1.5" style={{ color: colors.charcoal }}>
                    Apartment Shopping Challenge
                  </h4>
                  <p className="text-xs mb-4 flex-grow" style={{ color: colors.textMuted }}>
                    Compare 3 apartments. The cheapest isn&apos;t always the smartest.
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-bold group-hover:gap-2 transition-all"
                    style={{ color: colors.terracottaDark }}
                  >
                    Start Simulation <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              </div>
            </div>

            {/* Module 2: Money Matters */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
                  style={{ backgroundColor: colors.sage, color: colors.white }}
                >
                  Module 2
                </span>
                <h3 className="text-lg font-bold" style={{ color: colors.charcoal }}>
                  Money Matters
                </h3>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Link
                  to="/tools/move-in-reality-check"
                  className="group rounded-2xl p-5 border-2 transition-all hover:shadow-md flex flex-col"
                  style={{ borderColor: colors.primary, backgroundColor: colors.cream }}
                  data-testid="hub-tool-move-in-reality-check"
                >
                  <div
                    className="inline-block w-fit px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2"
                    style={{ backgroundColor: colors.sage + "33", color: colors.primary }}
                  >
                    Tool · 3 min
                  </div>
                  <h4 className="text-base font-bold mb-1.5" style={{ color: colors.charcoal }}>
                    Move-In Reality Check
                  </h4>
                  <p className="text-xs mb-4 flex-grow" style={{ color: colors.textMuted }}>
                    See the true upfront cost to move in — and how close you are to being ready.
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-bold group-hover:gap-2 transition-all"
                    style={{ color: colors.primary }}
                  >
                    Start the Check <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
                <Link
                  to="/resources/renters/calculator"
                  className="group rounded-2xl p-5 border-2 transition-all hover:shadow-md flex flex-col"
                  style={{ borderColor: colors.gold, backgroundColor: colors.ivory }}
                  data-testid="hub-tool-budget-calculator"
                >
                  <div
                    className="inline-block w-fit px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2"
                    style={{ backgroundColor: colors.gold + "44", color: colors.charcoal }}
                  >
                    Calculator · 5 min
                  </div>
                  <h4 className="text-base font-bold mb-1.5" style={{ color: colors.charcoal }}>
                    Monthly Budget Calculator
                  </h4>
                  <p className="text-xs mb-4 flex-grow" style={{ color: colors.textMuted }}>
                    See what rent you can afford long-term based on your real income and expenses.
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-bold group-hover:gap-2 transition-all"
                    style={{ color: colors.terracottaDark }}
                  >
                    Open Calculator <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              </div>
            </div>

            {/* Module 5: Life Skills — Case Studies */}
            <div className="mb-2">
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full"
                  style={{ backgroundColor: colors.terracotta, color: colors.white }}
                >
                  Module 5
                </span>
                <h3 className="text-lg font-bold" style={{ color: colors.charcoal }}>
                  Life Skills · Case Studies
                </h3>
              </div>
              <Link
                to="/stories/case-studies"
                className="group block rounded-2xl p-6 border-2 transition-all hover:shadow-md"
                style={{
                  borderColor: colors.primary,
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                  color: colors.white,
                }}
                data-testid="hub-case-studies"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <div
                      className="inline-block w-fit px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2"
                      style={{ backgroundColor: "rgba(255,255,255,0.2)", color: colors.gold }}
                    >
                      3 Real-Life Stories · 6 min each
                    </div>
                    <h4 className="text-xl font-bold mb-1">
                      Tanya, Marcus &amp; Maria — What Would YOU Do?
                    </h4>
                    <p className="text-sm opacity-90">
                      Adults learn from stories, not lectures. Every case study drops you into a
                      real decision — then reveals what actually worked.
                    </p>
                  </div>
                  <span
                    className="inline-flex items-center gap-2 text-sm font-bold group-hover:gap-3 transition-all whitespace-nowrap"
                    style={{ color: colors.gold }}
                  >
                    Read the Stories <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </div>

            <p
              className="text-xs mt-6 italic"
              style={{ color: colors.textMuted }}
            >
              Modules 3 (Housing Success) and 4 (Being a Great Neighbor)
              coming soon. New activities added every few weeks.
            </p>
          </section>

          {/* Closing CTA */}
          <div className="text-center max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold mb-3" style={{ color: colors.charcoal }}>
              Need help finding what you&apos;re looking for?
            </h3>
            <p className="mb-6" style={{ color: colors.textMuted }}>
              Our team can connect you with the right resource, partner, or program.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <Button
                  className="text-white rounded-full px-7 py-6 font-semibold"
                  style={{ backgroundColor: colors.primary }}
                >
                  Contact Our Team
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/partnerships">
                <Button
                  variant="outline"
                  className="rounded-full px-7 py-6 font-semibold border-2"
                  style={{ borderColor: colors.terracotta, color: colors.terracotta, backgroundColor: "transparent" }}
                >
                  Partnership Opportunities
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ResourceCenter;
